import React, { useState } from 'react';
import Popover from '@mui/material/Popover';
import Link from 'next/link';
import { KeyboardArrowDown } from '@mui/icons-material';
import { MenuItem } from '@mui/material';

type NavMenuProps = {
  name: string;
  isIcon: boolean;
  child?: {
    name: string;
    link: string;
    grandchild?: {
      name: string;
      link: string;
    }[];
  }[];
  hasUnderline?: boolean;
  className?: string;
  image?: string;
  menuKey?: MenuKey;
  menuKeys?: MenuKey[];
  menuItems?: Menu;
};

const NavMenu = ({
  name,
  isIcon,
  child,
  hasUnderline = true,
  className = '',
  image,
  menuKey,
  menuKeys,
  menuItems
}: NavMenuProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navMenu, setNavMenu] = useState<HTMLAnchorElement | null>(null);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const openMenu = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setMenuOpen(true);
  };

  return (
    <div className={`nav-menu${menuOpen ? '__open' : ''} ${className}`}>
      <a
        href={`#nav-menu-${name}`}
        aria-label={name}
        className='nav-link'
        onClick={(event) => openMenu(event)}
        ref={(node) => {
          setNavMenu(node);
        }}
      >
        <div className='nav-link__text'>
          <span
            className={isIcon ? 'nav-link__icon' : ''}
            style={
              image
                ? { backgroundImage: `url(${image})`, height: 24, width: 24 }
                : {}
            }
          >
            {isIcon ? null : name}
          </span>
          {!isIcon && (
            <span className='nav-link__expand-icon'>
              <KeyboardArrowDown />
            </span>
          )}
          {hasUnderline && <div className='nav-link__underline' />}
        </div>
      </a>
      <Popover
        anchorEl={navMenu}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={{
          minWidth: 200,
          borderRadius: '4px'
        }}
        slotProps={{
          paper: {
            style: {
              borderRadius: '4px'
            }
          }
        }}
      >
        <div className='nav-menu-container' style={{ borderRadius: '4px' }}>
          {child &&
            child.length > 0 &&
            child.map((childLink) => (
              <MenuItem
                key={childLink.name}
                onClick={closeMenu}
                className='nav-child-link'
                sx={{ padding: 0 }}
              >
                <Link
                  prefetch={false}
                  href={childLink.link}
                  className='nav-child-link'
                >
                  <div className='nav-link__text'>{childLink.name}</div>
                </Link>
                {childLink.grandchild && (
                  <div className='nav-menu-grandchild-container'>
                    {childLink.grandchild.map((grandchildLink) => (
                      <MenuItem
                        key={grandchildLink.name}
                        onClick={closeMenu}
                        className='nav-grandchild-link'
                        sx={{ padding: 0 }}
                      >
                        <Link
                          prefetch={false}
                          key={grandchildLink.name}
                          href={grandchildLink.link}
                          className='nav-grandchild-link'
                        >
                          <div className='nav-link__text'>
                            {grandchildLink.name}
                          </div>
                        </Link>
                      </MenuItem>
                    ))}
                  </div>
                )}
              </MenuItem>
            ))}
          {menuItems &&
            menuKey &&
            menuItems[menuKey].map((menuItem) => (
              <MenuItem
                key={menuItem?.name ?? 'key'}
                onClick={closeMenu}
                className='nav-child-link'
                sx={{ padding: 0 }}
              >
                <Link
                  prefetch={false}
                  href={menuItem?.slug ?? '#'}
                  className='nav-child-link'
                >
                  <div className='nav-link__text'>{menuItem?.name ?? ''}</div>
                </Link>
              </MenuItem>
            ))}
          {menuItems &&
            menuKeys?.map((key) => (
              <div key={key} className='sub-section'>
                {menuItems[key].map((menuItem) => (
                  <MenuItem
                    key={menuItem?.slug ?? 'key'}
                    onClick={closeMenu}
                    className='nav-child-link'
                    sx={{ padding: 0 }}
                  >
                    <Link
                      prefetch={false}
                      href={menuItem?.slug ?? '#'}
                      className='nav-child-link'
                      key={menuItem.slug ?? 'child'}
                    >
                      <div className='nav-link__text'>
                        {menuItem?.name ?? ''}
                      </div>
                    </Link>
                  </MenuItem>
                ))}
              </div>
            ))}
        </div>
      </Popover>
    </div>
  );
};

export default NavMenu;
